import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { useCoClient } from "../../data/store";
import {
  createCurrency,
  fetchSupportedCurrencies,
  type NewCurrency,
  type SupportedCurrenciesDTO,
  type SupportedCurrencyDTO,
} from "../../data/currenciesApi";
import { dbTrue, fetchZohoRecords, type ZohoCurrencyDTO } from "../../data/zohoRecordsApi";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { ComboPicker, type ComboOption } from "../../components/ComboPicker";
import { SubStepShell } from "../../components/SubStepShell";
import { cn } from "../../components/cn";
import { useSubStepNav } from "../useSubStepNav";

const INPUT_CLASS = "h-9 w-full rounded-md border bg-background px-2 text-sm";

/**
 * THIS SCREEN SAVES NO ANSWER, and used to.
 *
 * It asked which of the enabled currencies the client actually transacts in,
 * and stored the ticked codes under `verifySetup.currencies`. That question was
 * fine; the control was not. A tick beside a currency reads as the switch that
 * turns it on in Zoho, and it was not one — Zoho has no such switch, a currency
 * there being either present or deleted. Nothing read the answer either.
 *
 * If the question is wanted later it should be asked where the answer is used,
 * against whatever is known by then. Rows left over in `onboarding_answers` are
 * harmless and are not read by anything.
 */
function codeOf(currency: ZohoCurrencyDTO): string {
  return currency.currency_code ?? currency.currency_id;
}

/**
 * One currency the client's books already have.
 *
 * NOT A CONTROL. It was a tick list, which asked which currencies the client
 * transacts in — and that was a mistake, because a tick that looks like a
 * switch and switches nothing in Zoho is a question about the control rather
 * than an answer about the client. Zoho has no on and off for a currency
 * anyway: its own screen offers Edit and Delete, and the row it hands back
 * carries no active flag.
 *
 * NO FORMAT SAMPLE HERE, and it was tried. Zoho's `currency_format` holds a
 * specimen — "1,234,567.89" — meaning "this is the shape of a number in this
 * currency". Put in a row it reads as an amount or an exchange rate, which is
 * how the first person to see it read it. A bare number beside a currency is a
 * value; no amount of styling argues it out of that. It belongs on the add
 * form, where it sits under a label that says what it is.
 *
 * The symbol earns its place, being what prints on every invoice — but only
 * when it differs from the code. Zoho falls back to the code for currencies
 * with no glyph, so AED would otherwise render as "AED · UAE Dirham · AED" and
 * invite the same question.
 */
function CurrencyRow({
  currency,
  isBase,
  fresh,
}: {
  currency: ZohoCurrencyDTO;
  isBase: boolean;
  /** Added through this screen a moment ago, so it can be found in a long list. */
  fresh: boolean;
}) {
  const code = codeOf(currency);
  const raw = currency.currency_symbol ?? "";
  const symbol = raw === "" || raw === code ? null : raw;

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-md border px-3 py-2 text-sm",
        fresh ? "border-primary/40 bg-primary/5" : "border-border"
      )}
    >
      <span className="w-10 shrink-0 font-medium">{code}</span>
      <span className="min-w-0 flex-1 truncate text-muted-foreground">
        {currency.currency_name ?? ""}
      </span>

      {/* Chips before the symbol: the label goes ahead of the mark it labels,
          and the symbol stays last so it lines up down the column. */}
      {fresh && (
        <span className="shrink-0 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
          Just added
        </span>
      )}
      {isBase ? (
        <span className="shrink-0 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
          Base
        </span>
      ) : (
        // Auto is Zoho's default and the safe one. A manual rate is the
        // exception, and it is the one that quietly goes stale, so it is the
        // only one worth a chip.
        !dbTrue(currency.auto_exchange_rate_enabled) && (
          <span className="shrink-0 rounded-sm bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
            Manual rate
          </span>
        )
      )}

      <span className="w-4 shrink-0 text-right text-muted-foreground/80">{symbol ?? ""}</span>
    </li>
  );
}

interface CurrencyForm {
  code: string;
  symbol: string;
  name: string;
  precision: number;
  format: string;
}

const EMPTY_FORM: CurrencyForm = { code: "", symbol: "", name: "", precision: 2, format: "" };

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">
        {label}
        {hint && <span className="font-normal text-muted-foreground/70"> · {hint}</span>}
      </span>
      {children}
    </label>
  );
}

/**
 * Switch a currency on in the client's real Zoho Books.
 *
 * THE ONLY WRITE ON THIS SCREEN, and a narrow one on purpose. There is exactly
 * one thing a CA cannot otherwise do from here — invoice in a currency the
 * books do not have — and everything else about currencies stays in Zoho.
 * Disabling one, changing the base, setting a manual rate: none of it is part
 * of standing a client up.
 *
 * The five fields are Zoho's own New Currency dialog, in its order, because a
 * CA who has seen one should recognise the other. Picking a code fills the
 * other four from Zoho's defaults, which is where the value is: JPY has no
 * decimal places, KWD has three, and INR groups as 12,34,567.89 — none of it
 * guessable, all of it handed over by `meta/currencies`.
 */
function AddCurrency({
  clientId,
  supported,
  supportedError,
  enabled,
  onAdded,
}: {
  clientId: ClientId;
  supported: SupportedCurrenciesDTO | null;
  supportedError: string | null;
  /** The currencies the books already have, which are the ones not to offer. */
  enabled: ZohoCurrencyDTO[];
  onAdded: (currencies: ZohoCurrencyDTO[], code: string, rereadError: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CurrencyForm>(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  const already = useMemo(
    () => new Set(enabled.map((currency) => codeOf(currency).toUpperCase())),
    [enabled]
  );

  /**
   * The currencies worth offering.
   *
   * ZOHO DOES NOT DO THIS ITSELF — its own picker still offers AUD to an
   * organisation that already has AUD, checked on 60081360231. Filtering here
   * turns a duplicate from an error into something unpickable, which is the
   * better half of the same guard; the server refuses one anyway, in case this
   * list is a moment stale.
   */
  const options = useMemo<ComboOption[]>(() => {
    const rows = supported?.currencies ?? [];
    return rows
      .filter((currency) => !already.has(currency.code))
      .map((currency) => ({
        value: currency.code,
        label: currency.code,
        detail: currency.currency_name,
        note: `${currency.currency_symbol} · ${currency.currency_format}`,
        button: `${currency.code} - ${currency.currency_name}`,
        search: [currency.code, currency.currency_name, currency.currency_symbol],
      }));
  }, [supported, already]);

  const picked = (supported?.currencies ?? []).find(
    (candidate: SupportedCurrencyDTO) => candidate.code === form.code
  );

  /**
   * The formats to offer at the chosen precision.
   *
   * Zoho's general list plus the currency's own, because for at least one
   * currency the general list cannot produce it: INR groups as 12,34,567.89,
   * the lakh grouping, which is not among the four patterns `decimal_values`
   * publishes. Only at the currency's native precision — changing the decimal
   * places invalidates its format along with it. `CurrencyCreator` allows the
   * same set, so nothing offered here can be refused there.
   */
  const formatsFor = useCallback(
    (precision: number): string[] => {
      const general = supported?.formats[String(precision)] ?? [];
      if (
        picked === undefined ||
        picked.price_precision !== precision ||
        picked.currency_format === "" ||
        general.includes(picked.currency_format)
      ) {
        return general;
      }
      return [picked.currency_format, ...general];
    },
    [supported, picked]
  );

  const legalFormats = formatsFor(form.precision);

  // Zoho publishes 0, 2 and 3 and nothing between. Read off what it sent rather
  // than written here, so a fourth would appear on its own.
  const precisions = useMemo(
    () =>
      Object.keys(supported?.formats ?? {})
        .map(Number)
        .filter((value) => Number.isFinite(value))
        .sort((a, b) => a - b),
    [supported]
  );

  function pick(code: string) {
    const currency = (supported?.currencies ?? []).find(
      (candidate: SupportedCurrencyDTO) => candidate.code === code
    );
    if (currency === undefined) return;

    // Everything at once, the way Zoho's dialog does it. Nothing is preserved
    // from a previous pick: the symbol and name of the last currency are wrong
    // for this one, and a half-filled form is worse than a re-typed word.
    setForm({
      code,
      symbol: currency.currency_symbol,
      name: currency.currency_name,
      precision: currency.price_precision,
      format: currency.currency_format,
    });
  }

  function setPrecision(precision: number) {
    const legal = formatsFor(precision);
    setForm((current) => ({
      ...current,
      precision,
      // A 2-decimal format is not legal at 0 decimals, so moving the precision
      // has to move the format with it — which is what Zoho's dialog does by
      // refilling the list. Kept when it survives the move.
      format: legal.includes(current.format) ? current.format : (legal[0] ?? ""),
    }));
  }

  function reset() {
    setForm(EMPTY_FORM);
    setError(null);
  }

  const ready = form.code !== "" && form.symbol.trim() !== "" && form.name.trim() !== "";

  async function submit() {
    if (!ready || sending) return;

    setSending(true);
    setError(null);

    const payload: NewCurrency = {
      currency_code: form.code,
      currency_symbol: form.symbol.trim(),
      currency_name: form.name.trim(),
      price_precision: form.precision,
      currency_format: form.format,
    };

    try {
      const result = await createCurrency(clientId, payload);
      if (!liveRef.current) return;

      reset();
      setOpen(false);
      onAdded(result.currencies, result.created.currency_code, result.reread_error);
    } catch (caught: unknown) {
      if (!liveRef.current) return;
      // Zoho's own refusal comes through here and is worth more on screen than
      // anything we would write over it.
      setError(caught instanceof Error ? caught.message : "Could not add the currency.");
    } finally {
      if (liveRef.current) setSending(false);
    }
  }

  if (!open) {
    return (
      <div className="flex items-center gap-3">
        <CoButton onClick={() => setOpen(true)} disabled={supported === null}>
          Add a currency
        </CoButton>
        {supported === null && supportedError === null && (
          <span className="text-xs text-muted-foreground">Reading the currency list…</span>
        )}
        {supportedError !== null && (
          <span className="text-xs text-muted-foreground">{supportedError}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border bg-muted/20 p-4">
      <h4 className="text-sm font-semibold">New currency</h4>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Currency</span>
        <ComboPicker
          options={options}
          value={form.code}
          onChange={pick}
          placeholder="Select a currency"
          searchPlaceholder="Search - AED, dirham, ¥…"
          noun="currencies"
        />
      </div>

      {/* Zoho's own defaults, filled in and editable, exactly as its dialog
          leaves them. Worth showing rather than hiding behind a disclosure:
          the symbol is what appears on every invoice raised in this currency. */}
      <div className="grid gap-3 sm:grid-cols-[7rem_minmax(0,1fr)]">
        <Field label="Symbol">
          <input
            value={form.symbol}
            onChange={(event) => setForm((current) => ({ ...current, symbol: event.target.value }))}
            placeholder="₹"
            maxLength={10}
            className={INPUT_CLASS}
          />
        </Field>

        <Field label="Name">
          <input
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            placeholder="UAE Dirham"
            maxLength={100}
            className={INPUT_CLASS}
          />
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-[7rem_minmax(0,1fr)]">
        <Field label="Decimals">
          <select
            value={String(form.precision)}
            onChange={(event) => setPrecision(Number(event.target.value))}
            className={INPUT_CLASS}
          >
            {precisions.map((precision) => (
              <option key={precision} value={precision}>
                {precision}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Format" hint="how amounts are grouped">
          <select
            value={form.format}
            onChange={(event) => setForm((current) => ({ ...current, format: event.target.value }))}
            className={INPUT_CLASS}
          >
            {legalFormats.map((format) => (
              <option key={format} value={format}>
                {format}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      <div className="flex flex-wrap items-center gap-2">
        <CoButton onClick={() => void submit()} disabled={!ready || sending}>
          {sending ? "Adding…" : "Add to Zoho Books"}
        </CoButton>
        <CoButton
          variant="outline"
          onClick={() => {
            setOpen(false);
            reset();
          }}
          disabled={sending}
        >
          Cancel
        </CoButton>

        {/* One line saying exactly what is about to be created, so the last
            look before pressing the button is at the thing, not at the form. */}
        {ready && !sending && (
          <span className="text-xs text-muted-foreground">
            {form.code} · {form.symbol} · {form.format}
          </span>
        )}
      </div>
    </div>
  );
}

export function CurrencyStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "currency");

  const [currencies, setCurrencies] = useState<ZohoCurrencyDTO[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Zoho's whole catalogue, for the add form. Fetched alongside the client's
  // own list rather than when the form opens: it is a cached read either way,
  // and a CA who has decided to add a currency should not then wait for it.
  const [supported, setSupported] = useState<SupportedCurrenciesDTO | null>(null);
  const [supportedError, setSupportedError] = useState<string | null>(null);

  const [freshCode, setFreshCode] = useState<string | null>(null);
  const [rereadError, setRereadError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoadError(null);
    setSupportedError(null);

    fetchZohoRecords(clientId, ["currencies"])
      .then((records) => {
        if (live) setCurrencies(records.currencies ?? []);
      })
      .catch((error: unknown) => {
        if (live) setLoadError(error instanceof Error ? error.message : "Could not load the currencies.");
      });

    // Its own failure. Not being able to offer new currencies is no reason to
    // withhold the list of the ones the client has.
    fetchSupportedCurrencies(clientId)
      .then((result) => {
        if (live) setSupported(result);
      })
      .catch((error: unknown) => {
        if (!live) return;
        setSupportedError(
          error instanceof Error ? error.message : "Could not read Zoho's currency list."
        );
      });

    return () => {
      live = false;
    };
  }, [clientId]);

  function currencyAdded(next: ZohoCurrencyDTO[], code: string, reread: string | null) {
    setCurrencies(next);
    setFreshCode(code);
    setRereadError(reread);
  }

  if (!client) return null;

  const clientName = client.name || "this client";
  const rows = currencies ?? [];
  const base = rows.find((currency) => dbTrue(currency.is_base_currency)) ?? null;

  // Continue is the review sign-off, so the currencies have to actually be on
  // screen first — a read still in flight or one that failed is not a review.
  const reviewable = loadError === null && currencies !== null;

  return (
    <SubStepShell
      title="Currency Settings"
      description={`Below are the currencies already set up for ${clientName} in Zoho Books. The company's base currency is also shown below.`}
      thenWhat={reviewable ? "Continue → Set Up Customers & Vendors." : "The currencies have to load before this step can be signed off."}
      onContinue={() => void goNext()}
      continueDisabled={!reviewable}
    >
      {loadError !== null ? (
        <Callout variant="warning">{loadError}</Callout>
      ) : currencies === null ? (
        <p className="text-sm text-muted-foreground">Reading the currencies…</p>
      ) : rows.length === 0 ? (
        <Callout variant="warning">
          No currencies were read from {clientName}'s books. Re-run the read on Confirm Connection to Zoho.
        </Callout>
      ) : (
        <>
          {/*
            One line, not a section. The base currency is a fact read out of
            Zoho — it cannot be changed here, or anywhere once the books have
            transactions.
          */}
          <p className="text-sm">
            <span className="text-muted-foreground">Base currency: </span>
            {base === null ? (
              <span className="italic text-muted-foreground/70">not set in Zoho</span>
            ) : (
              <>
                <span className="font-medium">{codeOf(base)}</span>{" "}
                <span className="text-muted-foreground">{base.currency_name ?? ""}</span>
              </>
            )}
          </p>

          {/*
            No search. An organisation enables the handful of currencies it
            trades in — a dozen here, and the ones that matter are the odd ones,
            which is exactly what a filter would hide. The list is short enough
            to read.
          */}
          <div className="flex flex-col gap-3 border-t pt-4">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-base font-medium">Enabled in Zoho Books</h3>
              <span className="shrink-0 text-xs text-muted-foreground">
                {rows.length} {rows.length === 1 ? "currency" : "currencies"}
              </span>
            </div>

            <ul className="flex flex-col gap-1.5">
              {rows.map((currency) => (
                <CurrencyRow
                  key={currency.currency_id}
                  currency={currency}
                  isBase={dbTrue(currency.is_base_currency)}
                  fresh={codeOf(currency) === freshCode}
                />
              ))}
            </ul>
          </div>

          {/* The one write on this screen. */}
          <div className="flex flex-col gap-3 border-t pt-4">
            <div>
              <h3 className="text-base font-medium">Do you want to add a new currency?</h3>
              <p className="text-sm text-muted-foreground">
                If you do not see the currency you need and want to add a new currency to this account, use the Add
                a Currency button below.
              </p>
            </div>

            {rereadError !== null && <Callout variant="warning">{rereadError}</Callout>}

            <AddCurrency
              clientId={clientId}
              supported={supported}
              supportedError={supportedError}
              enabled={rows}
              onAdded={currencyAdded}
            />
          </div>

          {/*
            NO CLOSING NOTE about removing a currency. There was one, saying it
            is done in Zoho — a leftover from the tick list, which needed to
            explain why unticking did not reach the books. Nothing on the screen
            now offers to remove anything, so the note was answering a question
            it had itself raised.
          */}
        </>
      )}
    </SubStepShell>
  );
}
